import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody } from "@/components/admin/card";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { api } from "@/lib/api";
import { fmtFCFA } from "@/lib/format";
import { useCreateManualOrder } from "@/lib/orders/orders-hooks";
import type { DeliveryMode, PaymentMethod } from "@/lib/orders/types";

interface VariantOption {
	id: string;
	sku: string;
	name: string;
	defaultPrice: number;
}

interface CustomerOption {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	phone: string | null;
	/** b2c | b2b — une commande d'entreprise se facture avec NIU et RCCM. */
	type?: "b2c" | "b2b";
	/** Raison sociale, pour un client entreprise. */
	displayName?: string | null;
	/** « guest » : a commandé sans jamais ouvrir de compte. */
	status?: string;
}

interface HubOption {
	id: string;
	code: string;
	name: string;
	city: string;
}

interface CartLine {
	variantId: string;
	sku: string;
	name: string;
	quantity: number;
	unitPrice: number;
}

const STEPS = [
	"Client",
	"Articles",
	"Promo",
	"Adresses",
	"Livraison",
	"Paiement",
	"Récap",
	"Confirmation",
];

export function AdminOrderCreatePage() {
	const navigate = useNavigate();
	const create = useCreateManualOrder();
	const [step, setStep] = useState(0);

	const [customerQuery, setCustomerQuery] = useState("");
	const [customers, setCustomers] = useState<CustomerOption[]>([]);
	const [customer, setCustomer] = useState<CustomerOption | null>(null);

	const [productQuery, setProductQuery] = useState("");
	const [variants, setVariants] = useState<VariantOption[]>([]);
	const [cart, setCart] = useState<CartLine[]>([]);
	const [promoCode, setPromoCode] = useState("");
	const [promoAmount, setPromoAmount] = useState(0);

	const [hubs, setHubs] = useState<HubOption[]>([]);
	const [hubId, setHubId] = useState<string>("");

	const [recipientName, setRecipientName] = useState("");
	const [phone, setPhone] = useState("");
	const [city, setCity] = useState("Douala");
	const [streetLine, setStreetLine] = useState("");
	const [district, setDistrict] = useState("");

	const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("STANDARD");
	const [shippingAmount, setShippingAmount] = useState(2000);

	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");

	const [createdNumber, setCreatedNumber] = useState<string | null>(null);
	const [createdId, setCreatedId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api
			.get("/admin/catalog/hubs")
			.then((r) => {
				const list = r.data.data as HubOption[];
				setHubs(list);
				if (list.length > 0 && !hubId) setHubId(list[0].id);
			})
			.catch(() => {
				/*tolère*/
			});
	}, []); // eslint-disable-line

	useEffect(() => {
		if (customerQuery.length < 2) return;
		api
			.get(
				`/admin/orders/customer-search?q=${encodeURIComponent(customerQuery)}`,
			)
			.then((r) => {
				const users = (r.data?.data ?? []) as CustomerOption[];
				setCustomers(users);
			})
			.catch(() => setCustomers([]));
	}, [customerQuery]);

	useEffect(() => {
		if (productQuery.length < 2) return;
		api
			.get(
				`/admin/catalog/products?search=${encodeURIComponent(productQuery)}&pageSize=10`,
			)
			.then((r) => {
				type ApiProduct = {
					id: string;
					title: string;
					basePrice?: number;
					variants?: Array<{
						id: string;
						sku: string;
						name: string;
						priceOverride?: string | null;
						offers?: Array<{ price: string }>;
					}>;
				};
				const products = (r.data?.data?.items ?? []) as ApiProduct[];
				const flat: VariantOption[] = [];
				for (const p of products) {
					for (const v of p.variants ?? []) {
						const offerPrice = v.offers?.[0]?.price
							? Math.round(parseFloat(v.offers[0].price))
							: null;
						const overridePrice = v.priceOverride
							? Math.round(parseFloat(v.priceOverride))
							: null;
						const price = offerPrice ?? overridePrice ?? p.basePrice ?? 0;
						flat.push({
							id: v.id,
							sku: v.sku,
							name: `${p.title} — ${v.name}`,
							defaultPrice: price,
						});
					}
				}
				setVariants(flat);
			})
			.catch(() => setVariants([]));
	}, [productQuery]);

	const subtotal = cart.reduce((acc, c) => acc + c.unitPrice * c.quantity, 0);
	const total = subtotal + shippingAmount - promoAmount;

	const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
	const prev = () => setStep((s) => Math.max(0, s - 1));

	const addToCart = (v: VariantOption) => {
		setCart((c) => {
			const found = c.find((x) => x.variantId === v.id);
			if (found) {
				return c.map((x) =>
					x.variantId === v.id ? { ...x, quantity: x.quantity + 1 } : x,
				);
			}
			return [
				...c,
				{
					variantId: v.id,
					sku: v.sku,
					name: v.name,
					quantity: 1,
					unitPrice: v.defaultPrice,
				},
			];
		});
	};

	const submit = async () => {
		if (!customer || cart.length === 0 || !recipientName) {
			setError("Veuillez compléter client, articles et adresse.");
			return;
		}
		try {
			const order = await create.mutateAsync({
				customerId: customer.id,
				source: "ADMIN_MANUAL",
				lines: cart.map((c) => ({
					variantId: c.variantId,
					quantity: c.quantity,
					unitPriceOverride: c.unitPrice,
				})),
				deliveryMode,
				hubId: hubId || undefined,
				paymentMethod: paymentMethod || null,
				shippingAmount,
				discountAmount: promoAmount,
				shippingAddress: {
					recipientName,
					phone,
					city,
					streetLine,
					district,
				},
				asDraft: !paymentMethod,
			});
			setCreatedNumber(order.number);
			setCreatedId(order.id);
			setStep(STEPS.length - 1);
		} catch (e: unknown) {
			setError((e as Error).message);
		}
	};

	return (
		<>
			<PageHead
				title="Nouvelle commande"
				sub={`Étape ${step + 1} / ${STEPS.length} · ${STEPS[step]}`}
			/>

			<div
				style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}
			>
				{STEPS.map((s, i) => (
					<div
						key={s}
						data-testid={`step-${i}`}
						style={{
							padding: "4px 10px",
							borderRadius: 4,
							background:
								i === step
									? "var(--a-terracotta)"
									: i < step
										? "var(--a-success)"
										: "var(--a-border)",
							color: i <= step ? "#fff" : "var(--a-ink)",
							fontSize: 12,
						}}
					>
						{i + 1}. {s}
					</div>
				))}
			</div>

			{error && (
				<div style={{ background: "#fee", padding: 10, marginBottom: 12 }}>
					{error}
				</div>
			)}

			<Card>
				<CardBody>
					{step === 0 && (
						<div>
							<label>Rechercher un client (email/nom/tel)</label>
							<input
								type="search"
								value={customerQuery}
								onChange={(e) => setCustomerQuery(e.target.value)}
								placeholder="email@..."
								data-testid="customer-search"
								style={{
									width: "100%",
									padding: 8,
									border: "1px solid var(--a-border)",
									borderRadius: 6,
									marginBottom: 10,
								}}
							/>
							<ul style={{ listStyle: "none", padding: 0 }}>
								{customers.map((c) => (
									<li
										key={c.id}
										data-testid={`customer-option-${c.email}`}
										onClick={() => {
											setCustomer(c);
											setRecipientName(`${c.firstName} ${c.lastName}`);
											setPhone(c.phone ?? "");
										}}
										style={{
											padding: 8,
											cursor: "pointer",
											background:
												customer?.id === c.id
													? "var(--a-terra-soft)"
													: "transparent",
										}}
									>
										<strong>
											{c.displayName ?? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim()}
										</strong>{" "}
										— {c.email}
										{/*
										  Une commande d'entreprise se facture avec NIU et RCCM :
										  le découvrir après la saisie oblige à refaire la facture.
										*/}
										{c.type === "b2b" && (
											<Tag variant="info" noDot>
												Entreprise
											</Tag>
										)}
										{c.status === "guest" && (
											<Tag variant="ochre" noDot>
												Sans compte
											</Tag>
										)}
									</li>
								))}
							</ul>
							{customer && (
								<div style={{ marginTop: 10 }}>
									Sélectionné : <strong>{customer.email}</strong>
								</div>
							)}
						</div>
					)}

					{step === 1 && (
						<div>
							<label>Rechercher un produit</label>
							<input
								type="search"
								value={productQuery}
								onChange={(e) => setProductQuery(e.target.value)}
								placeholder="nom / sku..."
								data-testid="product-search"
								style={{
									width: "100%",
									padding: 8,
									border: "1px solid var(--a-border)",
									borderRadius: 6,
									marginBottom: 10,
								}}
							/>
							<ul style={{ listStyle: "none", padding: 0 }}>
								{variants.map((v) => (
									<li
										key={v.id}
										onClick={() => addToCart(v)}
										style={{ padding: 8, cursor: "pointer" }}
										data-testid={`variant-option-${v.sku}`}
									>
										+ {v.name} <span className="muted">({v.sku})</span> —{" "}
										{fmtFCFA(v.defaultPrice)}
									</li>
								))}
							</ul>
							<h4>Panier</h4>
							{cart.length === 0 ? (
								<div className="muted">Aucun article</div>
							) : (
								<table className="table">
									<thead>
										<tr>
											<th>SKU</th>
											<th>Nom</th>
											<th>Qté</th>
											<th>PU</th>
											<th>Total</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{cart.map((c) => (
											<tr key={c.variantId}>
												<td className="mono">{c.sku}</td>
												<td>{c.name}</td>
												<td>
													<input
														type="number"
														value={c.quantity}
														min={1}
														onChange={(e) =>
															setCart((arr) =>
																arr.map((x) =>
																	x.variantId === c.variantId
																		? {
																				...x,
																				quantity: Math.max(
																					1,
																					parseInt(e.target.value || "1", 10),
																				),
																			}
																		: x,
																),
															)
														}
														style={{ width: 60 }}
													/>
												</td>
												<td>{fmtFCFA(c.unitPrice)}</td>
												<td>{fmtFCFA(c.unitPrice * c.quantity)}</td>
												<td>
													<button
														onClick={() =>
															setCart((arr) =>
																arr.filter((x) => x.variantId !== c.variantId),
															)
														}
													>
														×
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
							<p>
								Sous-total : <strong>{fmtFCFA(subtotal)}</strong>
							</p>
						</div>
					)}

					{step === 2 && (
						<div>
							<label>Code promo</label>
							<input
								value={promoCode}
								onChange={(e) => setPromoCode(e.target.value)}
								placeholder="(optionnel)"
								style={{
									padding: 6,
									border: "1px solid var(--a-border)",
									marginRight: 8,
								}}
							/>
							<label>Montant (FCFA)</label>
							<input
								type="number"
								value={promoAmount}
								onChange={(e) =>
									setPromoAmount(parseInt(e.target.value || "0", 10))
								}
								min={0}
								style={{ padding: 6, border: "1px solid var(--a-border)" }}
							/>
							<div className="muted" style={{ marginTop: 8 }}>
								CSR plafonné à 5 000 FCFA. Manager illimité.
							</div>
						</div>
					)}

					{step === 3 && (
						<div style={{ display: "grid", gap: 10, maxWidth: 480 }}>
							<label>
								Nom destinataire{" "}
								<input
									value={recipientName}
									onChange={(e) => setRecipientName(e.target.value)}
									style={{
										width: "100%",
										padding: 8,
										border: "1px solid var(--a-border)",
									}}
								/>
							</label>
							<label>
								Téléphone{" "}
								<input
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									style={{
										width: "100%",
										padding: 8,
										border: "1px solid var(--a-border)",
									}}
								/>
							</label>
							<label>
								Ville{" "}
								<input
									value={city}
									onChange={(e) => setCity(e.target.value)}
									style={{
										width: "100%",
										padding: 8,
										border: "1px solid var(--a-border)",
									}}
								/>
							</label>
							<label>
								Quartier / district{" "}
								<input
									value={district}
									onChange={(e) => setDistrict(e.target.value)}
									style={{
										width: "100%",
										padding: 8,
										border: "1px solid var(--a-border)",
									}}
								/>
							</label>
							<label>
								Rue / repère{" "}
								<input
									value={streetLine}
									onChange={(e) => setStreetLine(e.target.value)}
									style={{
										width: "100%",
										padding: 8,
										border: "1px solid var(--a-border)",
									}}
								/>
							</label>
						</div>
					)}

					{step === 4 && (
						<div>
							<label>Mode livraison</label>
							<select
								value={deliveryMode}
								onChange={(e) =>
									setDeliveryMode(e.target.value as DeliveryMode)
								}
								style={{ padding: 8 }}
							>
								<option value="STANDARD">STANDARD</option>
								<option value="EXPRESS">EXPRESS</option>
								<option value="HUB_PICKUP">HUB_PICKUP (retrait)</option>
							</select>
							<br />
							<br />
							<label>Hub</label>
							<select
								value={hubId}
								onChange={(e) => setHubId(e.target.value)}
								style={{ padding: 8 }}
							>
								{hubs.map((h) => (
									<option key={h.id} value={h.id}>
										{h.name} ({h.city})
									</option>
								))}
							</select>
							<br />
							<br />
							<label>
								Frais livraison (FCFA){" "}
								<input
									type="number"
									value={shippingAmount}
									onChange={(e) =>
										setShippingAmount(parseInt(e.target.value || "0", 10))
									}
									style={{ padding: 6, border: "1px solid var(--a-border)" }}
								/>
							</label>
						</div>
					)}

					{step === 5 && (
						<div>
							<label>Mode de paiement</label>
							<select
								value={paymentMethod}
								onChange={(e) =>
									setPaymentMethod(e.target.value as PaymentMethod | "")
								}
								style={{ padding: 8 }}
							>
								<option value="">— (créer en DRAFT)</option>
								<option value="MOMO">MOMO</option>
								<option value="OM">Orange Money</option>
								<option value="BANK_TRANSFER">Virement bancaire</option>
								<option value="COD">Cash à la livraison</option>
								<option value="CARD">Carte</option>
							</select>
						</div>
					)}

					{step === 6 && (
						<div>
							<h4>Récap</h4>
							<div>
								Client :{" "}
								<strong>
									{customer?.firstName} {customer?.lastName}
								</strong>
							</div>
							<div>
								Articles : <strong>{cart.length}</strong> ({fmtFCFA(subtotal)})
							</div>
							<div>
								Livraison : <strong>{deliveryMode}</strong> (
								{fmtFCFA(shippingAmount)})
							</div>
							<div>
								Promo : <strong>{fmtFCFA(promoAmount)}</strong>
							</div>
							<div>
								Total : <strong>{fmtFCFA(total)}</strong>
							</div>
							<div>
								Paiement : <strong>{paymentMethod || "DRAFT"}</strong>
							</div>
							<div>
								Destinataire : <strong>{recipientName}</strong> / {phone} /{" "}
								{city}
							</div>
							<div style={{ marginTop: 10 }}>
								<AdminButton
									variant="primary"
									onClick={submit}
									data-testid="submit-order"
								>
									Créer la commande
								</AdminButton>
							</div>
						</div>
					)}

					{step === 7 && createdNumber && createdId && (
						<div>
							<h3>✓ Commande créée</h3>
							<p>
								Numéro : <strong>{createdNumber}</strong>
							</p>
							<AdminButton
								variant="primary"
								onClick={() => navigate(`/commandes/${createdId}`)}
							>
								Voir la commande
							</AdminButton>
						</div>
					)}
				</CardBody>
			</Card>

			<div style={{ marginTop: 16, display: "flex", gap: 8 }}>
				{step > 0 && step < STEPS.length - 1 && (
					<AdminButton variant="ghost" onClick={prev}>
						← Précédent
					</AdminButton>
				)}
				{step < STEPS.length - 2 && (
					<AdminButton variant="primary" onClick={next} data-testid="next-step">
						Suivant →
					</AdminButton>
				)}
			</div>
		</>
	);
}
